import { useEffect, useMemo, useState } from "react";

import { HealthInsuranceEligibilityClient } from "@/Common/Api/Patient/HealthInsuranceEligibilityClient";
import { HealthCardValidatorFactory, HealthCardValidatorRequest } from "@/Common/Components/Patient/HealthCardComponents/HealthCardValidator";
import { StatusComponent } from "@/Common/Components/Patient/StatusComponent";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { CommonStatus } from "@/Common/Models/Patient/Enum/CommonStatus";
import { HealthInsuranceEligibility } from "@/Common/Models/Patient/HealthInsuranceEligibility/HealthInsuranceEligibility";
import { HealthInsuranceEligibilityStatus } from "@/Common/Models/Patient/HealthInsuranceEligibility/HealthInsuranceEligibilityStatus";
import { CanadaState, CountryNames } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";

import styles from "./OntarioHealthInsurancePlanEligibilityIndicator.module.scss";

interface OntarioHealthInsurancePlanEligibilityIndicatorProps {
    patientHealthCardNumberWithVersion: string;
}

function OntarioHealthInsurancePlanEligibilityIndicator(
    props: Readonly<OntarioHealthInsurancePlanEligibilityIndicatorProps>
): JSX.Element {
    const { getText } = useTranslation();
    const [eligibility, setEligibility] = useState<HealthInsuranceEligibility>(null);

    const ontarioHealthCardValidator = useMemo(
        () => {
            const canadaLookupId = cacheStore.FindLookupByLookupCode(CountryNames.Canada).LookupID;
            const ontarioLookupId = cacheStore.FindLookupByLookupCode(CanadaState.Ontario).LookupID;
            return HealthCardValidatorFactory.getValidator(canadaLookupId, ontarioLookupId);
        },
        []
    );

    useEffect(
        () => {
            const action = async (): Promise<void> => {
                if (!!props.patientHealthCardNumberWithVersion
                    && ontarioHealthCardValidator.isValid(new HealthCardValidatorRequest(props.patientHealthCardNumberWithVersion))) {
                    const patientHealthCardProvinceLookupId = cacheStore.GetLookupIdForProvince(CanadaState.Ontario);

                    const response = await HealthInsuranceEligibilityClient
                        .getHealthInsuranceEligibility(props.patientHealthCardNumberWithVersion, null, patientHealthCardProvinceLookupId);

                    setEligibility(response);
                }
                else {
                    setEligibility(null);
                }
            };

            action();
        },
        [props.patientHealthCardNumberWithVersion, ontarioHealthCardValidator]
    );

    const getIconStatus = (): CommonStatus => {
        if (eligibility == null)
            return CommonStatus.Question;

        switch (eligibility.Eligibility) {
            case HealthInsuranceEligibilityStatus.Covered:
                return CommonStatus.Covered;
            case HealthInsuranceEligibilityStatus.NotCovered:
                return CommonStatus.NotCovered;
            case HealthInsuranceEligibilityStatus.MissingInformation:
            case HealthInsuranceEligibilityStatus.Pending:
            case HealthInsuranceEligibilityStatus.Unknown:
                return CommonStatus.Question;
            default:
                console.error("Unknown provincial health insurance plan eligibility status.", eligibility);
                return CommonStatus.Question;
        }
    };

    return (
        <StatusComponent
            status={getIconStatus()}
            statusInfo={getText("OntarioHealthInsurancePlanAbbrevLabel")}
            isBritishColumbia
            iconWrapperClassName={styles.ontarioEligibilityIcon}
        />
    );
}

export { OntarioHealthInsurancePlanEligibilityIndicator };
